import { Box, Button, SvgIcon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { primaryTint } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import ChatFlowIcon from 'assets/icons/chatFlow.svg';
import ArrowRightIcon from 'mdi-react/ArrowRightIcon';
import { bool, func, node, oneOf, string } from 'prop-types';
import React, { useMemo } from 'react';
import { I18n } from 'utils/i18n';

const ORDER = ['agent', 'skill', 'tool'];

const getStyles = theme => ({
  root: {
    marginTop: theme.spacing(-3),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(4.5),
    overflow: 'auto',
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(1.5),
    maxWidth: 560,
  },
  heroIcon: {
    fontSize: 88,
    marginBottom: theme.spacing(0.5),
  },
  heroTitle: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 700,
    letterSpacing: '-0.5px',
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  heroLead: {
    fontSize: theme.typography.pxToRem(14.5),
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    maxWidth: 520,
  },
  anatomyWrap: {
    width: '100%',
    maxWidth: 1040,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  anatomyLabel: {
    fontSize: theme.typography.pxToRem(10.5),
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  anatomyStrip: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  card: {
    flex: 1,
    minWidth: 0,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    padding: theme.spacing(2.25, 2.25, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    transition: 'all 200ms ease',
  },
  cardHighlight: {
    background: theme.palette.primary.ultraLight,
    border: `1.5px solid ${theme.palette.primary.main}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    minHeight: 44,
  },
  cardIconBox: {
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardIcon: {
    fontSize: 36,
    color: theme.palette.text.disabled,
    opacity: 0.5,
  },
  cardIconHighlight: {
    color: theme.palette.primary.main,
    opacity: 1,
  },
  waveIcon: {
    fontSize: 44,
  },
  waveIconMuted: {
    opacity: 0.3,
  },
  cardEyebrow: {
    fontSize: theme.typography.pxToRem(10.5),
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    opacity: 0.7,
  },
  cardEyebrowHighlight: {
    color: theme.palette.primary.main,
    opacity: 1,
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(15.5),
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginTop: '1px',
  },
  cardBlurb: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.55,
    color: theme.palette.text.secondary,
    margin: 0,
  },
  cardBlurbHighlight: {
    color: theme.palette.text.primary,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
  },
  chip: {
    display: 'inline-block',
    alignItems: 'center',
    padding: '4px 10px',
    fontSize: theme.typography.pxToRem(11.5),
    fontWeight: 500,
    borderRadius: '999px',
    background: theme.palette.primary.extraLight,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.extraLight}`,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, "Roboto Mono", monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  arrow: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    opacity: 0.5,
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      transform: 'rotate(90deg)',
      alignSelf: 'center',
    },
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  primaryBtn: {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '5px',
  },
  secondaryBtn: {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '5px',
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    background: theme.palette.background.paper,
    '&:hover': {
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.action.hover,
    },
  },
});

// Skill icon — stacked layers (matches FileTreeOutlineIcon vibes, but stylized)
const SkillIcon = ({ highlight = false, color, surfaceFill, tintFill, tintFillStrong }) => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden>
    <rect
      x="6"
      y="6"
      width="14"
      height="14"
      rx="3"
      stroke={color}
      strokeWidth="1.7"
      fill={highlight ? tintFill : 'transparent'}
    />
    <rect
      x="13"
      y="13"
      width="14"
      height="14"
      rx="3"
      stroke={color}
      strokeWidth="1.7"
      fill={highlight ? tintFillStrong : surfaceFill}
    />
    <rect
      x="20"
      y="20"
      width="10"
      height="10"
      rx="2.5"
      stroke={color}
      strokeWidth="1.7"
      fill={highlight ? color : 'transparent'}
      opacity={highlight ? 1 : 0.6}
    />
  </svg>
);

SkillIcon.propTypes = {
  highlight: bool,
  color: string.isRequired,
  surfaceFill: string.isRequired,
  tintFill: string.isRequired,
  tintFillStrong: string.isRequired,
};

// Tool icon — wrench
const ToolIcon = ({ highlight = false, color, tintFill }) => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden>
    <path
      d="M27.5 8.5a5.5 5.5 0 0 0-7.4 7.0L9.2 26.4a2.4 2.4 0 1 0 3.4 3.4L23.5 18.9a5.5 5.5 0 0 0 7.0-7.4l-3.1 3.1-3.0-3.0 3.1-3.1z"
      stroke={color}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={highlight ? tintFill : 'transparent'}
    />
  </svg>
);

ToolIcon.propTypes = {
  highlight: bool,
  color: string.isRequired,
  tintFill: string.isRequired,
};

const ConceptCard = ({
  kind,
  highlight,
  primaryColor,
  mutedColor,
  surfaceFill,
  tintFill,
  tintFillStrong,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const iconColor = highlight ? primaryColor : mutedColor;
  const eyebrowKey = highlight
    ? 'AgenticChatFlow.EmptyAnatomy.HereLabel'
    : `AgenticChatFlow.EmptyAnatomy.${kind}.Eyebrow`;

  let icon;
  if (kind === 'agent') {
    icon = (
      <SvgIcon sx={[styles.waveIcon, !highlight && styles.waveIconMuted]}>
        <ChatFlowIcon />
      </SvgIcon>
    );
  } else if (kind === 'skill') {
    icon = (
      <SkillIcon
        highlight={highlight}
        color={iconColor}
        surfaceFill={surfaceFill}
        tintFill={tintFill}
        tintFillStrong={tintFillStrong}
      />
    );
  } else {
    icon = <ToolIcon highlight={highlight} color={iconColor} tintFill={tintFill} />;
  }

  return (
    <Box sx={[styles.card, highlight && styles.cardHighlight]}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardIconBox}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            component="div"
            sx={[styles.cardEyebrow, highlight && styles.cardEyebrowHighlight]}
          >
            {I18n.t(eyebrowKey)}
          </Typography>
          <Typography component="div" sx={styles.cardTitle}>
            {I18n.t(`AgenticChatFlow.EmptyAnatomy.${kind}.Title`)}
          </Typography>
        </Box>
      </Box>
      <Typography component="p" sx={[styles.cardBlurb, highlight && styles.cardBlurbHighlight]}>
        {I18n.t(`AgenticChatFlow.EmptyAnatomy.${kind}.Blurb`)}
      </Typography>
      {highlight && (
        <Box sx={styles.chipRow}>
          {['1', '2', '3'].map(idx => (
            <Box
              key={idx}
              component="span"
              sx={styles.chip}
              data-testid={`agentic-empty-chip-${kind}-${idx}`}
            >
              {I18n.t(`AgenticChatFlow.EmptyAnatomy.${kind}.Examples.${idx}`)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

ConceptCard.propTypes = {
  kind: oneOf(['agent', 'skill', 'tool']).isRequired,
  highlight: bool.isRequired,
  primaryColor: string.isRequired,
  mutedColor: string.isRequired,
  surfaceFill: string.isRequired,
  tintFill: string.isRequired,
  tintFillStrong: string.isRequired,
};

const AgenticEmptyState = ({
  concept,
  title,
  lead,
  primaryLabel = null,
  primaryIcon = null,
  onPrimary = null,
  secondaryLabel = null,
  secondaryIcon = null,
  onSecondary = null,
  'data-testid': dataTestId = null,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const primaryColor = theme.palette.primary.main;
  const mutedColor = theme.palette.text.secondary;
  const surfaceFill = theme.palette.background.paper;
  const tintFill = primaryTint(theme, 0.1);
  const tintFillStrong = primaryTint(theme, 0.18);

  return (
    <Box sx={styles.root} data-testid={dataTestId || `agentic-empty-${concept}`}>
      <Box sx={styles.hero}>
        <SvgIcon sx={styles.heroIcon}>
          <ChatFlowIcon />
        </SvgIcon>
        <Typography component="h2" sx={styles.heroTitle}>
          {title}
        </Typography>
        <Typography component="p" sx={styles.heroLead}>
          {lead}
        </Typography>
      </Box>

      <Box sx={styles.anatomyWrap}>
        <Typography component="div" sx={styles.anatomyLabel}>
          {I18n.t('AgenticChatFlow.EmptyAnatomy.SectionLabel')}
        </Typography>
        <Box sx={styles.anatomyStrip}>
          {ORDER.map((kind, i) => (
            <React.Fragment key={kind}>
              <ConceptCard
                kind={kind}
                highlight={kind === concept}
                primaryColor={primaryColor}
                mutedColor={mutedColor}
                surfaceFill={surfaceFill}
                tintFill={tintFill}
                tintFillStrong={tintFillStrong}
              />
              {i < ORDER.length - 1 && (
                <Box sx={styles.arrow} aria-hidden>
                  <ArrowRightIcon size={22} />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {(primaryLabel || secondaryLabel) && (
        <Box sx={styles.actions}>
          {secondaryLabel && onSecondary && (
            <Button
              variant="outlined"
              color="secondary"
              sx={styles.secondaryBtn}
              startIcon={secondaryIcon}
              onClick={onSecondary}
            >
              {secondaryLabel}
            </Button>
          )}
          {primaryLabel && onPrimary && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              sx={styles.primaryBtn}
              startIcon={primaryIcon}
              onClick={onPrimary}
            >
              {primaryLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

AgenticEmptyState.propTypes = {
  concept: oneOf(['agent', 'skill', 'tool']).isRequired,
  title: string.isRequired,
  lead: string.isRequired,
  primaryLabel: string,
  primaryIcon: node,
  onPrimary: func,
  secondaryLabel: string,
  secondaryIcon: node,
  onSecondary: func,
  'data-testid': string,
};

export default AgenticEmptyState;
